import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import DescriptionIcon from '@mui/icons-material/Description';
import DoneIcon from '@mui/icons-material/Done';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import UploadIcon from '@mui/icons-material/Publish';

const bytesToSize = (bytes, decimals = 2, binary = false) => {
  if (bytes === 0) return '0 Bytes';
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const kb = !binary ? 1000 : 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizeCase = Math.floor(Math.log(bytes) / Math.log(kb));
  return `${parseFloat((bytes / kb ** sizeCase).toFixed(dm))} ${sizes[sizeCase]}`;
};

const getStyles = error => ({
  box: {
    alignItems: 'center',
    border: 1,
    borderColor: 'graphic.main',
    borderRadius: 0.5,
    borderStyle: 'dashed',
    display: 'flex',
    flexDirection: 'column',
    mt: 2,
    p: 3,
    ...(error && {
      borderColor: 'error.dark',
    }),
  },
  icon: {
    color: 'primary.light',
    fontSize: 38,
    mr: 1,
    ...(error && {
      color: 'error.light',
    }),
  },
  info: {
    color: 'text.disabled',
    ...(error && {
      color: 'error.main',
    }),
  },
  instructions: {
    color: error && 'error.dark',
  },
  labelIconRight: {
    fontSize: '20px',
    height: 20,
    width: 20,
  },
});

const preventDefault = e => {
  if (e?.preventDefault?.apply) e.preventDefault();
};

// prettier-ignore
const runIfAvailable = R.ifElse(
  R.prop('apply'), 
  R.binary(R.call), 
  R.identity
);

const DragAndDrop = ({
  label,
  maxSizeInBytes,
  onFileSelected,
  onInvalidFile,
  showDragZone,
  isLoading,
  isRequired,
  isDone,
  error,
}) => {
  const styles = getStyles(error);
  const fileInputRef = useRef();

  const triggerFileInput = () => {
    if (fileInputRef?.current?.click?.apply) {
      fileInputRef.current.click();
    }
  };

  // prettier-ignore
  // getFile :: Event -> File
  const getFile = R.either(
    R.path(['target', 'files', 0]), 
    R.path(['dataTransfer', 'files', 0])
  );

  // handleFile :: File -> Void
  const handleFile = R.ifElse(
    R.o(R.lte(R.__, maxSizeInBytes), R.prop('size')),
    runIfAvailable(onFileSelected),
    R.compose(
      runIfAvailable(onInvalidFile),
      R.assoc('file', R.__, { error: `Max file size ${bytesToSize(maxSizeInBytes)}` })
    )
  );

  // dragOverHandler :: Event -> Void
  const dragOverHandler = preventDefault;

  // prettier-ignore
  // dropHandler :: Event -> Void
  const dropHandler = R.o(
    R.tap(R.o(handleFile, getFile)), 
    R.tap(preventDefault)
  );

  // inputChangeHandler :: Event -> Void
  const inputChangeHandler = R.o(handleFile, getFile);

  const errorIcon = (
    <Tooltip
      arrow
      open
      placement="top"
      sx={{ backgroundColor: 'error.light', color: 'error.dark' }}
      title={error}
    >
      <Avatar sx={{ backgroundColor: 'error.light', height: 20, width: 20 }}>
        <PriorityHighIcon sx={{ color: 'error.dark', fontSize: 12 }} />
      </Avatar>
    </Tooltip>
  );

  const LabelIconRight = () => {
    if (isLoading) return <CircularProgress color="secondary" size={20} />;
    if (error) return errorIcon;
    if (isDone)
      return <UploadIcon color="secondary" onClick={triggerFileInput} sx={{ cursor: 'pointer' }} />;
    if (isRequired)
      return (
        <Avatar sx={{ backgroundColor: 'error.light', height: 20, width: 20 }}>
          <PriorityHighIcon sx={{ color: 'error.dark', fontSize: 12 }} />
        </Avatar>
      );

    return null;
  };

  return (
    <Box onDragOver={dragOverHandler} onDrop={dropHandler} sx={{ mb: 2 }}>
      <input
        onChange={inputChangeHandler}
        ref={fileInputRef}
        style={{ display: 'none' }}
        type="file"
      />

      <Box
        sx={{
          alignItems: 'center',
          backgroundColor: 'surface.white',
          display: 'flex',
          justifyContent: 'space-between',
          width: 1,
        }}
      >
        <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.5 }}>
          {isDone ? <DoneIcon color="secondary" /> : <DescriptionIcon />}
          <Typography variant="body1">{label}</Typography>
        </Box>
        <LabelIconRight />
      </Box>

      {showDragZone && (
        <Box sx={styles.box}>
          <Box sx={{ alignItems: 'center', display: 'flex', mb: 0.75, mr: 4.75 }}>
            <UploadIcon sx={styles.icon} />
            <Box>
              <Typography component="div" gutterBottom sx={styles.instructions} variant="caption">
                Drag files to upload or
              </Typography>
              {maxSizeInBytes && (
                <Typography sx={styles.info} variant="caption">
                  Max file size {bytesToSize(maxSizeInBytes)}
                </Typography>
              )}
            </Box>
          </Box>
          <Button onClick={triggerFileInput} variant="outlined">
            Browse
          </Button>
        </Box>
      )}
    </Box>
  );
};

DragAndDrop.propTypes = {
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
  isDone: PropTypes.bool,
  isLoading: PropTypes.bool,
  isRequired: PropTypes.bool,
  label: PropTypes.string.isRequired,
  maxSizeInBytes: PropTypes.number,
  onFileSelected: PropTypes.func,
  onInvalidFile: PropTypes.func,
  showDragZone: PropTypes.bool,
};

DragAndDrop.defaultProps = {
  error: undefined,
  isDone: false,
  isLoading: false,
  isRequired: false,
  maxSizeInBytes: 20000000,
  onFileSelected: undefined,
  onInvalidFile: undefined,
  showDragZone: true,
};

export default DragAndDrop;
